import { useState, useEffect, useCallback } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from '../../components/Layout'
import ClientHome from './ClientHome'
import ClientProfile from './ClientProfile'
import ClientScheduleSession from './ClientScheduleSession'
import ClientInterviews from './ClientInterviews'
import ClientSession from './ClientSession'
import ClientFeedback from './ClientFeedback'
import ClientPaymentSuccess from './ClientPaymentSuccess'
import { profileApi } from '../../lib/api/profile'
import { ProfileCompletionCtx, checkProfileComplete } from '../../contexts/profileCompletion'
import { useAuthStore } from '../../stores/authStore'
import OnboardingModal from '../../components/OnboardingModal'

function ProfileGuard({ isComplete, children }: { isComplete: boolean; children: React.ReactNode }) {
  if (!isComplete) return <Navigate to="/client/profile" replace />
  return <>{children}</>
}

export default function ClientDashboard() {
  const [isComplete, setIsComplete] = useState(true)
  const [checking, setChecking] = useState(true)
  const [showOnboarding, setShowOnboarding] = useState(false)

  const refresh = useCallback(() => {
    profileApi.getProfile()
      .then((data) => setIsComplete(checkProfileComplete(data?.data ?? data)))
      .catch(() => {})
  }, [])

  useEffect(() => {
    profileApi.getProfile()
      .then((data) => setIsComplete(checkProfileComplete(data?.data ?? data)))
      .catch(() => {})
      .finally(() => setChecking(false))

    const userId = useAuthStore.getState().user?.id
    if (userId && !localStorage.getItem(`idr_onboarding_${userId}`)) {
      setShowOnboarding(true)
    }
  }, [])

  const handleOnboardingDismiss = () => {
    const userId = useAuthStore.getState().user?.id
    if (userId) localStorage.setItem(`idr_onboarding_${userId}`, '1')
    setShowOnboarding(false)
  }

  if (checking) return null

  return (
    <ProfileCompletionCtx.Provider value={{ isComplete, refresh }}>
      <OnboardingModal open={showOnboarding} onDismiss={handleOnboardingDismiss} />
      <Layout role="client">
        <Routes>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<ProfileGuard isComplete={isComplete}><ClientHome /></ProfileGuard>} />
          <Route path="schedule" element={<ProfileGuard isComplete={isComplete}><ClientScheduleSession /></ProfileGuard>} />
          <Route path="interviews" element={<ProfileGuard isComplete={isComplete}><ClientInterviews /></ProfileGuard>} />
          <Route path="interviews/:source/:sourceId" element={<ProfileGuard isComplete={isComplete}><ClientSession /></ProfileGuard>} />
          <Route path="interviews/:id" element={<ProfileGuard isComplete={isComplete}><ClientSession /></ProfileGuard>} />
          <Route path="feedback" element={<ProfileGuard isComplete={isComplete}><ClientFeedback /></ProfileGuard>} />
          <Route path="profile" element={<ClientProfile />} />
          <Route path="payment-success" element={<ClientPaymentSuccess />} />
        </Routes>
      </Layout>
    </ProfileCompletionCtx.Provider>
  )
}
